import type { AuthUser } from '@stayon/auth-core';

/**
 * Wer in diesem Tab gerade wirkt: Benutzer, Workspace und — im Support — die Sitzung. Ein neuer
 * Access-Token desselben Benutzers ändert den Schlüssel nicht; ein Wechsel von Benutzer,
 * Workspace oder Support-Sitzung ändert ihn immer. Leer heißt: niemand angemeldet.
 */
export function identityKey(user: AuthUser | null): string {
  return user ? [user.userId, user.tenantId, user.supportSessionId ?? ''].join('|') : '';
}

export interface IdentityChange {
  previous: string;
  next: string;
  /**
   * Der Tab stellt beim Laden nur wieder her, was vor dem Neuladen galt. Dann bleiben Ablagen wie
   * die Objektauswahl stehen; jeder andere Wechsel beginnt ohne sie.
   */
  restored: boolean;
}

type IdentityListener = (change: IdentityChange) => void;

const listeners = new Set<IdentityListener>();

/** Läuft synchron, bevor die neue Identität sichtbar wird — also bevor React neu rendert. */
export function onIdentityChange(listener: IdentityListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Ein scheiternder Zuhörer hält den Wechsel nicht auf: sonst schickte der Tab schon mit dem Token
 * der neuen Identität, während er noch die alte zeigt.
 */
export function announceIdentityChange(change: IdentityChange): void {
  for (const listener of listeners) {
    try {
      listener(change);
    } catch (error) {
      const name = (error as { name?: unknown } | null)?.name;
      console.error('[auth] identity change listener failed', {
        name: typeof name === 'string' ? name : typeof error,
      });
    }
  }
}
